import {
  Application,
  CanvasFilterSystem,
  extensions,
  Ticker,
  UPDATE_PRIORITY,
} from "pixi.js";
import type { RenderProfile } from "@/lib/perf/perf-profile";
import { resolvePerfProfile } from "@/lib/perf/detect-perf-tier";

/**
 * Le système de filtres du rendu Canvas 2D, réenregistré à la main.
 *
 * Pixi enregistre le *pipe* de filtres pour ses trois rendus — WebGL, WebGPU
 * et Canvas — mais le *système* que ce pipe appelle n'est livré que pour les
 * deux premiers. `CanvasFilterSystem` ne s'enregistre que depuis son propre
 * module, que rien n'importe hors du baril `pixi.js`, et le paquet ne le
 * déclare pas dans `sideEffects` : le bundler le supprime. Le repli Canvas se
 * retrouve alors avec un pipe qui fait `renderer.filter.push(...)` sur un
 * `undefined`.
 *
 * Ce n'est pas théorique : en production, la première image levait
 * `Cannot read properties of undefined (reading 'push')` dès qu'un visiteur
 * sans WebGL ouvrait l'application, parce que la vignette du piano-roll et la
 * lueur de la tête de lecture portent chacune un filtre.
 *
 * Réenregistré ici, le système ignore proprement ce qu'il ne sait pas exécuter
 * — un avertissement, une fois par type de filtre. Nos deux effets sont écrits
 * en GLSL, donc inexécutables en Canvas de toute façon : on y perd l'effet,
 * plus l'écran. L'ajout est idempotent, Pixi déduplique par nom.
 */
extensions.add(CanvasFilterSystem);

/**
 * Extra time (ms) the renderer keeps drawing after the last activity, so the
 * final settled frame (a paused playhead, a committed scroll, a fresh layout)
 * is always presented before the ticker parks. ~15 frames at 60 Hz.
 */
const RENDER_IDLE_GRACE_MS = 250;

export class Engine {
  private application: Application;
  private startPromise: Promise<void> | null = null;
  private readonly render: RenderProfile;
  private resizeObserver: ResizeObserver | null = null;

  /**
   * Render-on-demand governor state. The Pixi ticker keeps re-rendering the
   * scene 60×/s as long as it runs, which pins the GPU even when nothing moves
   * (the idle-heat culprit). We park the ticker when idle and wake it on
   * activity. `activityProbe` reports continuous animations (playback, gesture,
   * inertia, scroll-to); `lastActiveAt` timestamps the last active frame or
   * explicit render request.
   */
  private activityProbe: () => boolean = () => false;
  private lastActiveAt = 0;

  /**
   * Présente-t-on encore des images ?
   *
   * Faux quand le canvas est entièrement recouvert. Le ticker, lui, continue de
   * tourner — c'est lui qui distribue le tick aux vues DOM qui se posent sur la
   * position du roll, la partition du parcours harmonique en tête, et l'arrêter
   * les figerait à l'endroit précis où le studio est caché. On retire donc le
   * rendu de Pixi du ticker, et rien d'autre.
   */
  private presenting = true;

  /**
   * Une taille reçue pendant qu'on ne présentait pas, et qu'il reste à poser.
   *
   * `Application.resize` **rend une image dans la foulée** — c'est dans Pixi, et
   * c'est exactement ce qu'on vient d'interdire. Tant que le canvas est
   * recouvert, sa taille n'intéresse personne : on note qu'elle a changé, et on
   * la pose en même temps que la première image d'après.
   */
  private resizePending = false;

  constructor(private container: HTMLElement, render?: RenderProfile) {
    this.application = new Application();
    this.render = render ?? resolvePerfProfile().render;
    (globalThis as Record<string, unknown>).__PIXI_APP__ = this.application;
  }

  async start() {
    const startPromise: Promise<void> = new Promise(async (resolve, reject) => {
      await this.application.init({
        antialias: this.render.antialias,
        autoDensity: true,
        // Ratio natif, plafonné à 2. En dessous de 2, les lignes de mesure
        // d'1 px et le texte bitmap scintillent au défilement : ils tombent à
        // cheval sur les pixels du backing store. Au-dessus, on n'y gagne
        // rien de visible mais le backing store grossit avec le carré du
        // ratio — un téléphone à DPR 3 rendrait 9× la surface CSS, pour un
        // coût GPU et mémoire que ces appareils n'ont pas.
        resolution: Math.min(window.devicePixelRatio, 2),
        resizeTo: this.container,
      });
      // No `maxFPS`: capping below the display refresh skips rAF frames
      // the browser would otherwise have presented, producing visible
      // micro-stutter — and 30 Hz scrolling reads as glitchy on a music
      // app. Mobile budget is recovered via audio + effects, not framerate.

      this.container.appendChild(this.application.canvas);

      // Pixi's `resizeTo` only reacts to *window* resize events, not to
      // layout-driven changes of the container itself (e.g. collapsing the
      // studio sidebar, which widens/narrows this element without resizing
      // the window). Observe the container directly and re-run Pixi's resize
      // so the canvas tracks its box. ResizeObserver batches per animation
      // frame, so this stays coalesced during the sidebar's collapse anim;
      // the actual layout work (doLayout → setSize) is cheap bookkeeping read
      // by the per-frame draw loop.
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.presenting) {
          this.resizePending = true;

          return;
        }

        this.application.resize();
        this.requestRender();
      });
      this.resizeObserver.observe(this.container);

      // Render on demand: park the ticker when nothing animates so the GPU
      // stops re-rendering a static scene 60×/s. The governor runs at the
      // lowest priority — after Pixi's own render — so the frame that ends an
      // interaction is always presented before we stop.
      this.lastActiveAt = performance.now();
      this.application.ticker.add(this.governTicker, this, UPDATE_PRIORITY.UTILITY);

      // Any pointer / wheel interaction on the canvas wakes the renderer.
      // Pixi's hit-testing and the gesture handlers are DOM-driven and fire
      // even while the ticker is parked; this brings it back so their effect
      // (pan, drag, click selection) is drawn.
      //
      // `pointermove` en fait partie parce que le survol change ce qui est
      // dessiné : la règle graduée écrit ses numéros de mesure sous le
      // pointeur, et c'est à l'arrêt qu'on va les lire — l'état où le ticker
      // est justement parqué. Un mouvement de souris n'est pas un repos, et il
      // n'y a pas d'image gratuite à en tirer : la fenêtre de grâce referme le
      // ticker un quart de seconde après le dernier mouvement.
      const canvas = this.application.canvas;
      canvas.addEventListener("pointerdown", this.wake, { passive: true });
      canvas.addEventListener("pointermove", this.wake, { passive: true });
      canvas.addEventListener("pointerup", this.wake, { passive: true });
      canvas.addEventListener("wheel", this.wake, { passive: true });

      resolve();
    });

    this.startPromise = startPromise;
  }

  /**
   * Report whether a continuous animation is in flight (transport playing,
   * gesture / inertia / scroll-to). While it returns true the ticker never
   * parks. Set once by the view after the render loop is registered.
   */
  setActivityProbe(probe: () => boolean): void {
    this.activityProbe = probe;
  }

  /**
   * Présenter, ou cesser de présenter.
   *
   * Cesser de présenter n'éteint pas le ticker : voir `presenting`. C'est le
   * rappel de rendu de Pixi qu'on retire, à la priorité où le greffon
   * `TickerPlugin` l'a posé. Reprendre le repose et redemande une image, sans
   * quoi la première image d'après resterait celle d'avant.
   */
  setPresenting(on: boolean): void {
    if (on === this.presenting) {
      return;
    }

    this.presenting = on;

    if (!on) {
      this.application.ticker.remove(this.application.render, this.application);

      return;
    }

    // Retiré avant d'être remis : `Ticker.add` n'écarte pas les doublons, et un
    // rappel posé deux fois rendrait deux fois par image. Le retrait d'un
    // rappel absent ne coûte rien, ce qui rend l'opération sûre à répéter.
    this.application.ticker.remove(this.application.render, this.application);
    this.application.ticker.add(this.application.render, this.application, UPDATE_PRIORITY.LOW);

    if (this.resizePending) {
      this.resizePending = false;
      this.application.resize();
    }

    this.requestRender();
  }

  /**
   * Ensure the renderer draws now and for a short grace window. Call after any
   * off-tick scene mutation (layout, selection, effect change, transport event)
   * so it is presented even when nothing else is animating.
   */
  requestRender(): void {
    this.lastActiveAt = performance.now();

    if (!this.application.ticker.started) {
      this.application.ticker.start();
    }
  }

  private wake = (): void => {
    this.requestRender();
  };

  /** Park the ticker once idle past the grace window; keep it alive otherwise. */
  private governTicker(): void {
    const now = performance.now();

    if (this.activityProbe()) {
      this.lastActiveAt = now;

      return;
    }

    if (now - this.lastActiveAt > RENDER_IDLE_GRACE_MS) {
      this.application.ticker.stop();
    }
  }

  async getApplication(): Promise<Application> {
    await this.startPromise;

    return this.application;
  }

  registerLoop(callback: (ticket: Ticker) => void) {
    this.application.ticker.add(callback);
  }

  async destroy() {
    await this.startPromise;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    const canvas = this.application.canvas;
    canvas.removeEventListener("pointerdown", this.wake);
    canvas.removeEventListener("pointermove", this.wake);
    canvas.removeEventListener("pointerup", this.wake);
    canvas.removeEventListener("wheel", this.wake);

    this.application.destroy(true, { children: true, texture: true });

    if ((globalThis as Record<string, unknown>).__PIXI_APP__ === this.application) {
      delete (globalThis as Record<string, unknown>).__PIXI_APP__;
    }
  }
}
